import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AppLoader from '../AppLoader/AppLoader';
import {
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import TextField from '../SanitizedTextField/SanitizedTextField';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ComplaintDetailedHistory from '../../pages/PowerComplaints/Complaints/ComplaintDetailedHistory';
import { fetchComplaintsWithHistory } from '../../utils/fetchComplaintHistory';
import { getDiscoCompanyMenuItems } from '../AdminCompanySelect/DiscoCompanyMenuItems';
import {
  ensureCompanySelectOptions,
  getCompanySelectOptions,
} from '../../services/companySelectOptions';
import { getApplicationTypes } from '../../network/APIs/APIs';
import { cfpBaseUrl } from '../../network/urls';
import './SearchPopup.scss';

// Service types come only from comman/getApplicationTypes (see fetchSearchApplicationTypes)
let cachedSearchServiceOptions = [];
let pendingSearchServiceOptions = null;

export const mapApplicationTypesToSearchOptions = (rows = []) =>
  (Array.isArray(rows) ? rows : [])
    .filter((row) => row && row.activeFlag !== false && row.applicationTypeId != null)
    .sort((a, b) => Number(a.orderBy || 0) - Number(b.orderBy || 0))
    .map((row) => {
      const initial = String(row.idInitial || '').replace(/-/g, '').toUpperCase();
      return {
        id: row.applicationTypeId,
        label: row.applicationType || initial,
        prefix: initial ? `${initial}-` : '',
        idLength: row.idlength ?? row.idLength ?? null,
      };
    })
    .filter((item) => item.prefix);

export const getSearchServiceOptions = () => cachedSearchServiceOptions;

/** Legacy MCO search id was 5; getApplicationTypes uses 2. */
export const normalizeSearchApplicationTypeId = (typeId) => {
  const id = String(typeId ?? '');
  if (id === '5') return '2';
  return id;
};

export const resolveSearchApplicationTypeId = (typeId, searchValue = '') => {
  const options = getSearchServiceOptions();
  const value = String(searchValue || '').trim().toUpperCase();
  const prefixLetters = (value.match(/^([A-Z]{2,4})-/) || [])[1];
  if (prefixLetters) {
    const fromPrefix = options.find(
      (item) => String(item.prefix || '').replace(/-/g, '').toUpperCase() === prefixLetters,
    );
    if (fromPrefix) return Number(fromPrefix.id);
  }

  const normalized = normalizeSearchApplicationTypeId(typeId);
  const fromId = options.find((item) => String(item.id) === String(normalized));
  if (fromId) return Number(fromId.id);

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : Number(typeId);
};

/** Loads service types from the API; concurrent callers share one request. */
export const fetchSearchApplicationTypes = (token) => {
  if (!token) return Promise.resolve(cachedSearchServiceOptions);
  if (!pendingSearchServiceOptions) {
    pendingSearchServiceOptions = getApplicationTypes(token)
      .then((rows) => {
        const mapped = mapApplicationTypesToSearchOptions(rows);
        // A failed call keeps the last loaded list (and is retried on the next call)
        if (mapped.length) cachedSearchServiceOptions = mapped;
        return cachedSearchServiceOptions;
      })
      .finally(() => {
        pendingSearchServiceOptions = null;
      });
  }
  return pendingSearchServiceOptions;
};

export const DEFAULT_SEARCH_SERVICE_TYPE = '1';

const SEARCH_PATH_SERVICE_TYPE = {
  'new-connection-table': '1',
  'new-connection-form': '1',
  'meter-change-request-table': '2',
  'meter-change-request-form': '2',
  'meter-change-request-update': '2',
  'due-date-extension-table': '7',
  'due-date-extension-form': '7',
  'installments-table': '8',
  'installments-form': '8',
  'meter-reading-correction-table': '11',
  'meter-reading-correction-form': '11',
  'mrc-bill': '11',
  'rco-fixed-charges-bill': '13',
  'reconnection-table': '13',
  'reconnection-form': '13',
  'reconnection-update': '13',
  'disconnection-table': '14',
  'disconnection-form': '14',
  'disconnection-update': '14',
  'change-of-attributes-table': '15',
  'change-of-attributes-form': '15',
  'change-of-attributes-update': '15',
  'adjust-last-bill-table': '16',
  'adjust-last-bill-form': '16',
  'adjust-last-bill-update': '16',
  'amend-2636-bill-table': '17',
  'amend-2636-bill-form': '17',
  'part-payment-update': '17',
  'advance-payment-table': '18',
  'advance-payment-form': '18',
  'advance-payment-update': '18',
  'advance-payment-bill': '18',
  'withdrawal-of-installment-table': '19',
  'withdrawal-of-installment-form': '19',
};

export const getSearchServiceTypeFromPath = (pathname = '') => {
  const segments = String(pathname || '')
    .toLowerCase()
    .split('/')
    .filter(Boolean);
  for (const segment of segments) {
    if (SEARCH_PATH_SERVICE_TYPE[segment]) {
      return normalizeSearchApplicationTypeId(SEARCH_PATH_SERVICE_TYPE[segment]);
    }
  }
  return DEFAULT_SEARCH_SERVICE_TYPE;
};

export const SEARCH_APPLICATION_ID_NUMERIC_LENGTH = 14;
export const SEARCH_APPLICATION_ID_LONG_NUMERIC_LENGTH = 15;

const getServiceLettersFromPrefix = (prefix = '') =>
  (String(prefix).match(/^[A-Za-z]+/) || [''])[0].toUpperCase();

/** Numeric digits after the service prefix. Total ID length includes prefix. */
export const getSearchApplicationIdNumericLengthFromPrefix = (prefix = '') => {
  const letters = getServiceLettersFromPrefix(prefix);
  const hyphenPrefix = letters ? `${letters}-` : '';
  const match = getSearchServiceOptions().find(
    (item) => getServiceLettersFromPrefix(item.prefix) === letters,
  );
  const totalLength = match?.idLength;
  if (totalLength) return Math.max(totalLength - hyphenPrefix.length, 1);
  return letters.length >= 3
    ? SEARCH_APPLICATION_ID_LONG_NUMERIC_LENGTH
    : SEARCH_APPLICATION_ID_NUMERIC_LENGTH;
};

export const getSearchApplicationIdNumericLength = (typeId) =>
  getSearchApplicationIdNumericLengthFromPrefix(getSearchServicePrefix(typeId));

export const getSearchApplicationIdOfficeDigits = ({
  isAdmin = false,
  officeCode = '',
  hierarchy,
  selectedCompanyCode = '',
} = {}) => {
  if (isAdmin) {
    return String(selectedCompanyCode || '').replace(/\D/g, '').slice(0, 2);
  }

  const code = String(officeCode || '').replace(/\D/g, '');
  const level = Number(hierarchy);

  // Auth hierarchy: 1/2 company, 3 circle, 4/51 division, 5/52 subdivision.
  if (level === 1 || level === 2) return code.slice(0, 2);
  if (level === 3) return code.slice(0, 3);
  if (level === 4 || level === 51) return code.slice(0, 4);
  if (level === 5 || level === 52) return code.slice(0, 5);

  if (code.length >= 5) return code.slice(0, 5);
  if (code.length === 4) return code.slice(0, 4);
  if (code.length === 3) return code.slice(0, 3);
  return code.slice(0, 2);
};

export const getSearchServicePrefix = (typeId) => {
  const match = getSearchServiceOptions().find(
    (item) => String(item.id) === normalizeSearchApplicationTypeId(typeId),
  );
  return match?.prefix || '';
};

export const getSearchApplicationIdRemainingLength = (officeContext = {}, typeId) => {
  const officeDigits = getSearchApplicationIdOfficeDigits(officeContext);
  const numericLength = getSearchApplicationIdNumericLength(typeId);
  return Math.max(numericLength - officeDigits.length, 1);
};

export const getSearchApplicationIdPrefix = (typeId, officeContext = {}) => {
  const servicePrefix = getSearchServicePrefix(typeId);
  if (!servicePrefix) return '';
  const officeDigits = getSearchApplicationIdOfficeDigits(officeContext);
  return `${servicePrefix}${officeDigits}`;
};

export const PASTED_APPLICATION_COMPANY_MISMATCH_MESSAGE =
  'Your pasted application does not belong to this company.';

export const getApplicationIdCompanyDigits = (value) => {
  let next = String(value || '').replace(/\s/g, '').toUpperCase();
  next = next.replace(/^[A-Z]{2,4}-/, '');
  return next.replace(/[^0-9]/g, '').slice(0, 2);
};

export const isFullApplicationIdPaste = (value, prefix = '') => {
  const raw = String(value || '').replace(/\s/g, '').toUpperCase();
  if (/^[A-Z]{2,4}-/.test(raw)) return true;
  const digits = raw.replace(/[^0-9]/g, '');
  const numericLength = getSearchApplicationIdNumericLengthFromPrefix(prefix);
  return digits.length >= numericLength;
};

export const doesPastedApplicationBelongToCompany = (value, prefix = '') => {
  const officeDigits = String(prefix).replace(/^[A-Za-z]+-/, '');
  const expectedCompany = officeDigits.slice(0, 2);
  if (expectedCompany.length < 2) return true;
  if (!isFullApplicationIdPaste(value, prefix)) return true;
  const pastedCompany = getApplicationIdCompanyDigits(value);
  if (pastedCompany.length < 2) return true;
  return pastedCompany === expectedCompany;
};

export const stripSearchApplicationIdPrefix = (value, prefix = '') => {
  let next = String(value || '').replace(/\s/g, '').toUpperCase();
  const normalizedPrefix = String(prefix || '').toUpperCase();
  const officeDigits = String(prefix).replace(/^[A-Za-z]+-/, '');
  const hadServicePrefix = /^[A-Z]{2,4}-/.test(next);
  const numericLength = getSearchApplicationIdNumericLengthFromPrefix(prefix);

  if (normalizedPrefix && next.startsWith(normalizedPrefix)) {
    return next.slice(normalizedPrefix.length).replace(/[^0-9]/g, '');
  }

  next = next.replace(/^[A-Z]{2,4}-/, '');
  const digits = next.replace(/[^0-9]/g, '');
  if (!officeDigits) return digits;

  const remainingLength = Math.max(numericLength - officeDigits.length, 1);
  // Strip office digits only for a full application ID. Remaining digits often
  // start with the same office code (NC-13 + 131260909004), so do not strip
  // just because the typed suffix starts with those digits.
  if (hadServicePrefix || digits.length >= numericLength) {
    return digits.slice(officeDigits.length);
  }

  if (digits.length > remainingLength && digits.length - officeDigits.length === remainingLength) {
    return digits.slice(officeDigits.length);
  }

  return digits;
};

export const normalizeSearchApplicationIdInput = (value, prefix = '', maxLength) => {
  const suffix = stripSearchApplicationIdPrefix(value, prefix);
  if (!maxLength) return suffix;
  return suffix.slice(0, maxLength);
};

export const composeSearchApplicationId = (typeId, enteredId, officeContext = {}) => {
  const prefix = getSearchApplicationIdPrefix(typeId, officeContext);
  const suffix = stripSearchApplicationIdPrefix(enteredId, prefix);
  if (!suffix) return '';
  return `${prefix}${suffix}`;
};

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    padding: '0px 14px',
  },
};

const noBrowserAutocomplete = {
  autoComplete: 'off',
  autoCorrect: 'off',
  autoCapitalize: 'off',
  spellCheck: 'false',
  readOnly: true,
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
  'data-form-type': 'other',
};

const unlockAutocomplete = (event) => {
  event.target.removeAttribute('readonly');
};

const TECHNICAL_SEARCH_TYPES = [
  {
    id: 'reference',
    label: 'Reference Number',
    length: 14,
    searchType: 'referenceNo',
    placeholder: 'Enter 14-digit reference number',
  },
  {
    id: 'ccms',
    label: 'CCMS Complaint ID',
    length: 14,
    searchType: 'complaintId',
    placeholder: 'Enter 14-digit CCMS Complaint ID',
  },
  {
    id: 'eops',
    label: 'e-Ops Complaint ID',
    prefix: 'PC-',
    length: 15,
    searchType: 'complaintId',
    placeholder: 'Enter 15-digit ID',
  },
];

const APMS_SEARCH = {
  id: 'apms',
  label: 'APMS',
  prefix: 'APMS-',
  length: 14,
  searchType: 'apms',
  placeholder: 'Enter 14-digit APMS ID',
};

const mapApmsSearchToDetail = (item = {}, history = []) => {
  const pick = (...values) => {
    const found = values.find(
      (value) => value !== undefined && value !== null && String(value).trim() !== '' && String(value) !== '-',
    );
    return found ?? '';
  };

  const lineComplaintHistory = (Array.isArray(history) ? history : []).map((event) => ({
    Status: pick(event.status, event.Status),
    ModificationDateTime: pick(event.OperationDateTime, event.modificationDateTime, event.registeredAt),
    ModifiedBy: pick(event.modifiedBy, event.OperationBy, event.registeredBy),
    Remarks: pick(event.linemanRemarks, event.remarks, event.description),
    UserFullName: pick(event.allocatedTo, event.escalatedTo),
  }));

  return {
    EOpsTrackingId: pick(item.eOpsTrackingId),
    TicketNo: pick(item.ticketNo),
    Status: pick(item.status),
    TransformerName: pick(item.transformerName),
    FeederCode: pick(item.feederName, item.feederCode),
    CompanyCode: pick(item.companyCode),
    CircleCode: pick(item.circleCode),
    DivisionCode: pick(item.divisionCode),
    SubdivisionCode: pick(item.subdivisionCode, item.subDivisionCode),
    UserFullName: pick(item.allocatedTo, item.escalatedTo, item.modifiedBy),
    RegisteredAt: item.registeredAt || '',
    RegisteredBy: pick(item.registeredBy),
    StartTime: item.startTime || item.allocatedAt || '',
    AllocationDateTime: item.allocatedAt || '',
    EndTime: item.endTime || '',
    ModifiedBy: pick(item.modifiedBy),
    ModificationDateTime: item.modificationDateTime || '',
    ComplaintType: pick(item.complaintTypeName),
    ComplaintCategory: pick(item.complaintCategoryName),
    Source: pick(item.source),
    ComplaintDescription: pick(item.description, item.complaintDescription, item.linemanRemarks),
    LinemanRemarks: pick(item.linemanRemarks, item.remarks),
    lineComplaintHistory,
  };
};

const fetchApmsComplaintDetail = async (token, trackingId) => {
  const response = await fetch(`${cfpBaseUrl}apms/searchAPMSComplaint`, {
    method: 'POST',
    headers: {
      Authorization: token,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ eOpsTrackingId: trackingId }),
    redirect: 'follow',
  });
  const result = await response.json();
  const item = Array.isArray(result?.data) ? result.data[0] : null;
  if ((result?.status === '200' || result?.status === 200) && item) {
    return mapApmsSearchToDetail(item, result?.History || result?.history || []);
  }
  return null;
};

const SearchPopup = ({
  searchBarrPopupOpen,
  handleCloseSearchBarPopup,
  selectedTab,
  handleTabChange,
  tabLabels,
  searchFormik,
  isAdmin = false,
  canTechnicalSearch = false,
}) => {
  const { code: officeCode, hierarchy, token } = useSelector((state) => state.auth);
  const [applicationIdPasteError, setApplicationIdPasteError] = useState('');
  const [serviceOptions, setServiceOptions] = useState(getSearchServiceOptions);
  const [searchTrack, setSearchTrack] = useState('commercial');
  const [technicalFamily, setTechnicalFamily] = useState('technical');
  const [technicalType, setTechnicalType] = useState('reference');
  const [technicalValue, setTechnicalValue] = useState('');
  const [technicalError, setTechnicalError] = useState('');
  const [technicalSearching, setTechnicalSearching] = useState(false);
  const [technicalResult, setTechnicalResult] = useState(null);

  useEffect(() => {
    if (!searchBarrPopupOpen) return;
    setSearchTrack('commercial');
    setTechnicalFamily('technical');
    setTechnicalType('reference');
    setTechnicalValue('');
    setTechnicalError('');
    if (token && isAdmin) {
      ensureCompanySelectOptions(token).catch(() => {});
    }
  }, [searchBarrPopupOpen, token, isAdmin]);

  useEffect(() => {
    if (!searchBarrPopupOpen) return undefined;
    let cancelled = false;
    fetchSearchApplicationTypes(token).then((options) => {
      if (cancelled) return;
      setServiceOptions(options);
      const currentType = String(searchFormik.values.iv_application_type || '');
      if (!currentType || !options.length) return;
      const normalized = normalizeSearchApplicationTypeId(currentType);
      if (!options.some((opt) => String(opt.id) === normalized)) {
        // Page's default service isn't offered by the API — make the user pick one
        searchFormik.setFieldValue('iv_application_type', '');
      } else if (normalized !== currentType) {
        searchFormik.setFieldValue('iv_application_type', normalized);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [searchBarrPopupOpen, token]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const tabCount = tabLabels.length;

  const goToPrevTab = () => {
    const next = selectedTab <= 0 ? tabCount - 1 : selectedTab - 1;
    handleTabChange(null, next);
  };

  const goToNextTab = () => {
    const next = selectedTab >= tabCount - 1 ? 0 : selectedTab + 1;
    handleTabChange(null, next);
  };

  const fieldError =
    applicationIdPasteError ||
    (selectedTab === 0 && searchFormik.touched.iv_reference_no && searchFormik.errors.iv_reference_no) ||
    (selectedTab === 1 && searchFormik.touched.iv_application_id && searchFormik.errors.iv_application_id) ||
    (selectedTab === 2 && searchFormik.touched.iv_cnic && searchFormik.errors.iv_cnic) ||
    (selectedTab === 3 && searchFormik.touched.iv_applicant_name && searchFormik.errors.iv_applicant_name) ||
    '';
  const serviceError =
    searchFormik.touched.iv_application_type && searchFormik.errors.iv_application_type
      ? searchFormik.errors.iv_application_type
      : '';
  const companyError =
    isAdmin &&
      searchFormik.touched.iv_company_code &&
      searchFormik.errors.iv_company_code
      ? searchFormik.errors.iv_company_code
      : '';
  const hasService = Boolean(searchFormik.values.iv_application_type);
  // Empty until the API list contains the selected id (avoids an out-of-range Select value while loading)
  const normalizedServiceId = normalizeSearchApplicationTypeId(
    searchFormik.values.iv_application_type,
  );
  const selectedServiceValue = serviceOptions.some(
    (item) => String(item.id) === normalizedServiceId,
  )
    ? normalizedServiceId
    : '';
  const hasCompany = Boolean(searchFormik.values.iv_company_code);
  const canSubmit = hasService && (!isAdmin || hasCompany);
  const applicationIdOfficeContext = {
    isAdmin,
    officeCode,
    hierarchy,
    selectedCompanyCode: searchFormik.values.iv_company_code,
  };
  const applicationIdPrefix = getSearchApplicationIdPrefix(
    searchFormik.values.iv_application_type,
    applicationIdOfficeContext,
  );
  const applicationIdRemainingLength = getSearchApplicationIdRemainingLength(
    applicationIdOfficeContext,
    searchFormik.values.iv_application_type,
  );

  const handleApplicationIdChange = (event) => {
    setApplicationIdPasteError('');
    const nextValue = normalizeSearchApplicationIdInput(
      event.target.value,
      applicationIdPrefix,
      applicationIdRemainingLength,
    );
    searchFormik.setFieldValue('iv_application_id', nextValue, true);
  };

  const handleApplicationIdPaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') || '';
    if (!doesPastedApplicationBelongToCompany(pasted, applicationIdPrefix)) {
      searchFormik.setFieldValue('iv_application_id', '', false);
      searchFormik.setFieldTouched('iv_application_id', true, false);
      setApplicationIdPasteError(PASTED_APPLICATION_COMPANY_MISMATCH_MESSAGE);
      return;
    }
    setApplicationIdPasteError('');
    const nextValue = normalizeSearchApplicationIdInput(
      pasted,
      applicationIdPrefix,
      applicationIdRemainingLength,
    );
    searchFormik.setFieldValue('iv_application_id', nextValue);
  };

  const renderField = () => {
    if (selectedTab === 0) {
      return (
        <TextField
          fullWidth
          variant="outlined"
          name="iv_reference_no"
          id="iv_reference_no"
          placeholder="Enter reference number"
          value={searchFormik.values.iv_reference_no}
          onChange={searchFormik.handleChange}
          onBlur={searchFormik.handleBlur}
          autoComplete="off"
          onFocus={unlockAutocomplete}
          inputProps={{ ...noBrowserAutocomplete, maxLength: 14 }}
          sx={textFieldSx}
        />
      );
    }
    if (selectedTab === 1) {
      const suffixMaxLength = applicationIdPrefix
        ? applicationIdRemainingLength
        : getSearchApplicationIdNumericLength(searchFormik.values.iv_application_type);
      return (
        <div className={`search-popup__id-input${applicationIdPrefix ? ' has-prefix' : ''}`}>
          {applicationIdPrefix ? (
            <span className="search-popup__id-prefix">{applicationIdPrefix}</span>
          ) : null}
          <TextField
            fullWidth
            variant="outlined"
            placeholder={
              applicationIdPrefix
                ? `Enter ${suffixMaxLength}-digit ID`
                : 'Enter application ID'
            }
            name="iv_application_id"
            id="iv_application_id"
            value={searchFormik.values.iv_application_id}
            onChange={handleApplicationIdChange}
            onPaste={handleApplicationIdPaste}
            onBlur={searchFormik.handleBlur}
            autoComplete="off"
            onFocus={unlockAutocomplete}
            inputProps={{ ...noBrowserAutocomplete, maxLength: suffixMaxLength }}
            sx={textFieldSx}
          />
        </div>
      );
    }
    if (selectedTab === 2) {
      return (
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Enter CNIC"
          name="iv_cnic"
          id="iv_cnic"
          value={searchFormik.values.iv_cnic}
          onChange={searchFormik.handleChange}
          onBlur={searchFormik.handleBlur}
          autoComplete="off"
          onFocus={unlockAutocomplete}
          inputProps={{ ...noBrowserAutocomplete, maxLength: 13 }}
          sx={textFieldSx}
        />
      );
    }
    return (
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Enter applicant name"
        name="iv_applicant_name"
        id="iv_applicant_name"
        value={searchFormik.values.iv_applicant_name}
        onChange={searchFormik.handleChange}
        onBlur={searchFormik.handleBlur}
        autoComplete="off"
        onFocus={unlockAutocomplete}
        inputProps={{ ...noBrowserAutocomplete, minLength: 3 }}
        sx={textFieldSx}
      />
    );
  };

  const technicalIndex = Math.max(
    0,
    TECHNICAL_SEARCH_TYPES.findIndex((item) => item.id === technicalType),
  );
  const selectedTechnical = TECHNICAL_SEARCH_TYPES[technicalIndex] || TECHNICAL_SEARCH_TYPES[0];
  const activeSearch = technicalFamily === 'apms' ? APMS_SEARCH : selectedTechnical;

  const selectTechnicalFamily = (family) => {
    setTechnicalFamily(family);
    setTechnicalValue('');
    setTechnicalError('');
  };

  const selectTechnicalType = (index) => {
    const next = TECHNICAL_SEARCH_TYPES[index] || TECHNICAL_SEARCH_TYPES[0];
    setTechnicalType(next.id);
    setTechnicalValue('');
    setTechnicalError('');
  };

  const shiftTechnicalType = (step) => {
    const count = TECHNICAL_SEARCH_TYPES.length;
    selectTechnicalType((technicalIndex + step + count) % count);
  };

  const normalizeTechnicalDigits = (raw, selected = activeSearch) => {
    let text = String(raw || '');
    const prefix = String(selected.prefix || '');
    if (prefix && text.toUpperCase().startsWith(prefix.toUpperCase())) {
      text = text.slice(prefix.length);
    }
    return text.replace(/\D/g, '').slice(0, selected.length);
  };

  const handleTechnicalSearch = async (event) => {
    event.preventDefault();
    const digits = normalizeTechnicalDigits(technicalValue);
    if (digits.length !== activeSearch.length) {
      setTechnicalError(`${activeSearch.label} must be ${activeSearch.length} digits.`);
      return;
    }
    const value = `${activeSearch.prefix || ''}${digits}`;

    const isApms = activeSearch.searchType === 'apms';
    setTechnicalError('');
    setTechnicalSearching(true);
    handleCloseSearchBarPopup();
    setTechnicalResult({
      searchValue: value,
      searchLabel: activeSearch.label,
      loading: true,
      complaint: null,
      matched: [],
      error: '',
      apms: isApms,
    });

    try {
      if (isApms) {
        const [, complaint] = await Promise.all([
          new Promise((resolve) => {
            window.setTimeout(resolve, 450);
          }),
          fetchApmsComplaintDetail(token, value),
        ]);
        setTechnicalResult({
          searchValue: value,
          searchLabel: activeSearch.label,
          loading: false,
          complaint,
          matched: [],
          error: complaint ? '' : 'No complaint found for this APMS ID.',
          apms: true,
        });
      } else {
        const [{ complaints, message }] = await Promise.all([
          fetchComplaintsWithHistory(token, activeSearch.searchType, value),
          new Promise((resolve) => {
            window.setTimeout(resolve, 450);
          }),
        ]);
        const rows = Array.isArray(complaints) ? complaints : [];
        setTechnicalResult({
          searchValue: value,
          searchLabel: activeSearch.label,
          loading: false,
          complaint: rows.length === 1 ? rows[0] : null,
          matched: rows.length > 1 ? rows : [],
          error: rows.length ? '' : (message || 'No complaint found.'),
          apms: false,
        });
      }
    } catch {
      setTechnicalResult({
        searchValue: value,
        searchLabel: activeSearch.label,
        loading: false,
        complaint: null,
        matched: [],
        error: 'Unable to search. Please try again.',
        apms: isApms,
      });
    } finally {
      setTechnicalSearching(false);
    }
  };

  const closeTechnicalResult = () => setTechnicalResult(null);

  return (
    <>
    <Dialog
      open={searchBarrPopupOpen}
      onClose={handleCloseSearchBarPopup}
      maxWidth={isMobile ? false : 'md'}
      fullWidth={isMobile}
      disableScrollLock
      className={`search-popup${isMobile ? ' search-popup--mobile' : ''}${isAdmin ? ' search-popup--admin' : ''}`}
      sx={
        isMobile
          ? {
            '& .MuiDialog-paper': {
              borderRadius: '12px',
            },
          }
          : {
            '& .MuiDialog-container': {
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
              padding: '72px 24px 24px',
            },
            '& .MuiDialog-paper': {
              margin: 0,
              borderRadius: '14px',
              width: 'auto',
              minWidth: `min(${isAdmin || canTechnicalSearch ? 760 : 620}px, calc(100vw - 48px))`,
              maxWidth: `min(${isAdmin || canTechnicalSearch ? 820 : 680}px, calc(100vw - 48px))`,
            },
          }
      }
    >
      <DialogContent className="search-popup__content">
        {canTechnicalSearch ? (
          <div className="search-popup__track" role="tablist" aria-label="Search track">
            <button
              type="button"
              role="tab"
              aria-selected={searchTrack === 'commercial'}
              className={`search-popup__track-btn${searchTrack === 'commercial' ? ' is-active' : ''}`}
              onClick={() => setSearchTrack('commercial')}
            >
              Commercial
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={searchTrack === 'technical'}
              className={`search-popup__track-btn${searchTrack === 'technical' ? ' is-active' : ''}`}
              onClick={() => setSearchTrack('technical')}
            >
              Technical
            </button>
          </div>
        ) : null}
        {canTechnicalSearch && searchTrack === 'technical' ? (
          <>
            <div className="search-popup__track search-popup__family" role="tablist" aria-label="Technical family">
              <button
                type="button"
                role="tab"
                aria-selected={technicalFamily === 'technical'}
                className={`search-popup__track-btn${technicalFamily === 'technical' ? ' is-active' : ''}`}
                onClick={() => selectTechnicalFamily('technical')}
              >
                Line Complaint
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={technicalFamily === 'apms'}
                className={`search-popup__track-btn${technicalFamily === 'apms' ? ' is-active' : ''}`}
                onClick={() => selectTechnicalFamily('apms')}
              >
                APMS
              </button>
            </div>
            {technicalFamily === 'technical' ? (
            isMobile ? (
              <div className="search-popup__tab-nav" role="tablist" aria-label="Technical search type">
                <IconButton
                  type="button"
                  className="search-popup__tab-nav-btn"
                  onClick={() => shiftTechnicalType(-1)}
                  aria-label="Previous search type"
                  size="small"
                >
                  <ChevronLeftIcon fontSize="medium" />
                </IconButton>
                <button
                  type="button"
                  className="search-popup__tab-nav-label"
                  onClick={() => shiftTechnicalType(1)}
                  aria-label={`Current: ${selectedTechnical.label}. Tap for next.`}
                >
                  {selectedTechnical.label}
                </button>
                <IconButton
                  type="button"
                  className="search-popup__tab-nav-btn"
                  onClick={() => shiftTechnicalType(1)}
                  aria-label="Next search type"
                  size="small"
                >
                  <ChevronRightIcon fontSize="medium" />
                </IconButton>
              </div>
            ) : (
              <div className="search-popup__tabs-wrap">
                <Tabs
                  className="search-popup__tabs"
                  value={technicalIndex}
                  onChange={(_, next) => selectTechnicalType(next)}
                  textColor="inherit"
                  variant="standard"
                >
                  {TECHNICAL_SEARCH_TYPES.map((item) => (
                    <Tab key={item.id} label={item.label} />
                  ))}
                </Tabs>
              </div>
            )
            ) : null}
            <form
              onSubmit={handleTechnicalSearch}
              className="search-popup__form"
              autoComplete="off"
            >
              <div className="search-popup__bar">
                <div className="search-popup__field">
                  <div className={activeSearch.prefix ? 'search-popup__id-input has-prefix' : undefined}>
                    {activeSearch.prefix ? (
                      <span className="search-popup__id-prefix">{activeSearch.prefix}</span>
                    ) : null}
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder={activeSearch.placeholder}
                      value={technicalValue}
                      onChange={(event) => {
                        setTechnicalValue(normalizeTechnicalDigits(event.target.value));
                        setTechnicalError('');
                      }}
                      onPaste={(event) => {
                        const pasted = event.clipboardData?.getData('text') || '';
                        if (!pasted) return;
                        event.preventDefault();
                        setTechnicalValue(normalizeTechnicalDigits(pasted));
                        setTechnicalError('');
                      }}
                      autoComplete="off"
                      onFocus={unlockAutocomplete}
                      inputProps={{
                        ...noBrowserAutocomplete,
                        maxLength: activeSearch.length,
                        inputMode: 'numeric',
                      }}
                      sx={textFieldSx}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="search-popup__submit-btn"
                  disabled={technicalSearching}
                >
                  <SearchRoundedIcon />
                  Go
                </button>
              </div>
              {technicalError ? <p className="search-popup__error">{technicalError}</p> : null}
            </form>
          </>
        ) : (
        <>
        <div className="search-popup__heading">
          <span className="search-popup__heading-title">Search Application</span>
          <span className="search-popup__heading-hint">
            {isAdmin ? 'Please select a company and a service.' : 'Please select a service.'}
          </span>
        </div>
        {isMobile ? (
          <div className="search-popup__tab-nav" role="tablist" aria-label="Search type">
            <IconButton
              type="button"
              className="search-popup__tab-nav-btn"
              onClick={goToPrevTab}
              aria-label="Previous search type"
              size="small"
            >
              <ChevronLeftIcon fontSize="medium" />
            </IconButton>
            <button
              type="button"
              className="search-popup__tab-nav-label"
              onClick={goToNextTab}
              aria-label={`Current: ${tabLabels[selectedTab]}. Tap for next.`}
            >
              {tabLabels[selectedTab]}
            </button>
            <IconButton
              type="button"
              className="search-popup__tab-nav-btn"
              onClick={goToNextTab}
              aria-label="Next search type"
              size="small"
            >
              <ChevronRightIcon fontSize="medium" />
            </IconButton>
          </div>
        ) : (
          <div className="search-popup__tabs-wrap">
            <Tabs
              className="search-popup__tabs"
              value={selectedTab}
              onChange={handleTabChange}
              textColor="inherit"
              variant="standard"
            >
              {tabLabels.map((label, index) => (
                <Tab key={index} label={label} />
              ))}
            </Tabs>
          </div>
        )}
        <form
          onSubmit={searchFormik.handleSubmit}
          className="search-popup__form"
          autoComplete="off"
        >
          <input
            type="text"
            name="search_popup_autofill_trap"
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
            className="search-popup__autofill-trap"
          />
          <div className="search-popup__bar">
            {isAdmin ? (
              <div className={`search-popup__service search-popup__company${companyError ? ' is-error' : ''}${hasCompany ? '' : ' is-empty'}`}>
                <CorporateFareOutlinedIcon className="search-popup__service-icon" />
                <Select
                  id="iv_company_code"
                  name="iv_company_code"
                  value={searchFormik.values.iv_company_code || ''}
                  onChange={(event) => {
                    setApplicationIdPasteError('');
                    searchFormik.handleChange(event);
                  }}
                  onBlur={searchFormik.handleBlur}
                  displayEmpty
                  variant="standard"
                  disableUnderline
                  className="search-popup__service-select"
                  renderValue={(selected) => {
                    if (!selected) return 'Select company *';
                    const match = getCompanySelectOptions({ includePitc: true }).find(
                      (item) => String(item.value) === String(selected)
                    );
                    return match?.label || 'Select company *';
                  }}
                  MenuProps={{
                    PaperProps: { className: 'search-popup__service-menu' },
                  }}
                >
                  <MenuItem value="">
                    <em>Select company *</em>
                  </MenuItem>
                  {getDiscoCompanyMenuItems(
                    searchFormik.values.iv_company_code,
                    true,
                    false
                  )}
                </Select>
              </div>
            ) : null}
            <div className={`search-popup__service${serviceError ? ' is-error' : ''}${hasService ? '' : ' is-empty'}`}>
              <AppsRoundedIcon className="search-popup__service-icon" />
              <Select
                id="iv_application_type"
                name="iv_application_type"
                value={selectedServiceValue}
                onChange={(event) => {
                  searchFormik.setFieldValue(
                    'iv_application_type',
                    normalizeSearchApplicationTypeId(event.target.value),
                  );
                }}
                onBlur={searchFormik.handleBlur}
                displayEmpty
                variant="standard"
                disableUnderline
                className="search-popup__service-select"
                renderValue={(selected) => {
                  if (!selected) return 'Select service *';
                  const match = serviceOptions.find(
                    (item) => String(item.id) === String(selected)
                  );
                  return match?.label || 'Select service *';
                }}
                MenuProps={{
                  PaperProps: { className: 'search-popup__service-menu' },
                }}
              >
                {serviceOptions.map((item) => (
                  <MenuItem key={item.id} value={String(item.id)}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div className="search-popup__field">{renderField()}</div>
            <button
              type="submit"
              className="search-popup__submit-btn"
              disabled={!canSubmit}
              onClick={() => {
                if (isAdmin && !hasCompany) {
                  searchFormik.setFieldTouched('iv_company_code', true, true);
                }
                if (!hasService) {
                  searchFormik.setFieldTouched('iv_application_type', true, true);
                }
              }}
            >
              <SearchRoundedIcon />
              Go
            </button>
          </div>
          {(companyError || serviceError || fieldError) && (
            <p className="search-popup__error">{companyError || serviceError || fieldError}</p>
          )}
        </form>
        </>
        )}
      </DialogContent>
    </Dialog>
    <Dialog
      open={Boolean(technicalResult)}
      onClose={technicalResult?.loading ? undefined : closeTechnicalResult}
      maxWidth="lg"
      fullWidth
      disableScrollLock
      className="technical-result"
    >
      {technicalResult ? (
        <>
          <header className="technical-result__head">
            <div className="technical-result__head-copy">
              <p className="technical-result__kicker">{technicalResult.searchLabel || 'Technical'}</p>
              <h2>Complaint History</h2>
              <div className="technical-result__meta">
                <span>{technicalResult.searchLabel}</span>
                <strong>{technicalResult.searchValue}</strong>
              </div>
            </div>
            <IconButton
              type="button"
              className="technical-result__close"
              onClick={closeTechnicalResult}
              disabled={technicalResult.loading}
              aria-label="Close complaint history"
              size="small"
            >
              <CloseRoundedIcon />
            </IconButton>
          </header>
          <DialogContent className="technical-result__body">
            {technicalResult.loading ? (
              <div className="technical-result__loading" role="status" aria-live="polite">
                <AppLoader onLight label="Searching complaint..." />
              </div>
            ) : (
              <ComplaintDetailedHistory
                embedded
                apms={Boolean(technicalResult.apms)}
                onClose={closeTechnicalResult}
                initialComplaintId={technicalResult.searchValue}
                preloadedComplaint={technicalResult.complaint}
                preloadedMatched={technicalResult.matched}
                preloadedError={technicalResult.error}
              />
            )}
          </DialogContent>
        </>
      ) : null}
    </Dialog>
    </>
  );
};

export default SearchPopup;
